import { useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    LinearProgress,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { format } from "date-fns";
import LaudoVisita from "entity/LaudoVisita";
import adicionarAcimaAbaixoLaudo from "entity/Indicador/util/adicionarAcimaAbaixoLaudo";
import { getQueryMassUpdate } from "entity/LaudoVisita/components/LaudoVisitaConsultaRelatorios/functions";

// Classificação de cada laudo após o processamento em memória (dry-run), antes de gravar.
const SITUACAO = {
    COM_ALTERACOES: "com_alteracoes",
    SEM_ALTERACOES: "sem_alteracoes",
    SEM_PARAMETROS: "sem_parametros",
    ERRO: "erro",
};

const CHIP_SITUACAO = {
    [SITUACAO.COM_ALTERACOES]: { label: "Com alterações", color: "warning" },
    [SITUACAO.SEM_ALTERACOES]: { label: "Já preenchido", color: "success" },
    [SITUACAO.SEM_PARAMETROS]: { label: "Sem parâmetros históricos", color: "error" },
    [SITUACAO.ERRO]: { label: "Erro ao processar", color: "error" },
};

export default function AdicionarAcimaAbaixoLote() {
    const [dataInicio, setDataInicio] = useState("2026-01-01");
    const [dataFim, setDataFim] = useState(format(new Date(), "yyyy-MM-dd"));
    const [status, setStatus] = useState("");

    const [buscando, setBuscando] = useState(false);
    const [erroBusca, setErroBusca] = useState("");
    const [preview, setPreview] = useState(null);

    const [dialogAberto, setDialogAberto] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [progresso, setProgresso] = useState({ atual: 0, total: 0 });
    const [mensagem, setMensagem] = useState(null);

    const limpar = () => {
        setPreview(null);
        setErroBusca("");
        setMensagem(null);
    };

    // Processa um único laudo em memória (sem gravar) e o classifica.
    //
    // LEVE DE PROPÓSITO: opera direto sobre os dados brutos do documento (`doc.data()`), sem instanciar a
    // entidade nem chamar `loadData`/`onLoad`. Isso evita, por laudo, o carregamento das imagens (base64
    // de cada apontamento/coleta e do parecer técnico) e as leituras ao vivo de Fazenda e Cliente — nada
    // disso é usado para calcular acima/abaixo. Como não chamamos `loadFazenda`, o `data.cliente` continua
    // sendo o snapshot histórico salvo no laudo, que é exatamente o que o cálculo precisa.
    const processarLaudo = (data) => {
        const resultado = adicionarAcimaAbaixoLaudo(data, data.cliente);

        let situacao;
        if (resultado.semParametrosHistoricos) {
            situacao = SITUACAO.SEM_PARAMETROS;
        } else if (resultado.totalAlteracoes === 0) {
            situacao = SITUACAO.SEM_ALTERACOES;
        } else {
            situacao = SITUACAO.COM_ALTERACOES;
        }

        return {
            uid: data.uid,
            sequencialIndex: data.sequencialIndex,
            clienteNome: data.cliente?.nome ?? "-",
            fazendaNome: data.fazenda?.nomeFazenda ?? "-",
            situacao,
            totalAlteracoes: resultado.totalAlteracoes ?? 0,
            resultado,
        };
    };

    const handleBuscar = async () => {
        limpar();

        if (!dataInicio || !dataFim) {
            setErroBusca("Informe a data de início e a data de fim.");
            return;
        }
        if (dataInicio > dataFim) {
            setErroBusca("A data de início não pode ser posterior à data de fim.");
            return;
        }

        setBuscando(true);
        try {
            const query = getQueryMassUpdate(null, status || null, dataInicio, dataFim);
            const snapshot = await query.get();

            if (snapshot.empty) {
                setErroBusca("Nenhum laudo encontrado no período informado.");
                return;
            }

            const resultados = [];
            // Cálculo em memória sobre os dados já retornados pela query — sem I/O adicional por laudo.
            for (const doc of snapshot.docs) {
                const data = doc.data();
                data.uid = doc.id;

                if (data.status === "Cancelado") continue;

                try {
                    resultados.push(processarLaudo(data));
                } catch (error) {
                    console.error(`Erro ao processar laudo Nº ${data.sequencialIndex}:`, error);
                    resultados.push({
                        uid: doc.id,
                        sequencialIndex: data.sequencialIndex,
                        clienteNome: data.cliente?.nome ?? "-",
                        fazendaNome: data.fazenda?.nomeFazenda ?? "-",
                        situacao: SITUACAO.ERRO,
                        totalAlteracoes: 0,
                        resultado: null,
                    });
                }
            }

            resultados.sort((a, b) => (a.sequencialIndex ?? 0) - (b.sequencialIndex ?? 0));
            setPreview(resultados);
        } catch (error) {
            console.error("Erro ao buscar laudos para adicionar acima/abaixo:", error);
            setErroBusca("Erro ao buscar os laudos. Tente novamente.");
        } finally {
            setBuscando(false);
        }
    };

    const laudosComAlteracoes = preview ? preview.filter((l) => l.situacao === SITUACAO.COM_ALTERACOES) : [];
    const totalAlteracoes = laudosComAlteracoes.reduce((sum, l) => sum + l.totalAlteracoes, 0);

    const contarPorSituacao = (situacao) => (preview ? preview.filter((l) => l.situacao === situacao).length : 0);

    const handleConfirmarSalvar = async () => {
        if (!laudosComAlteracoes.length) return;

        setSalvando(true);
        setProgresso({ atual: 0, total: laudosComAlteracoes.length });

        const falhas = [];
        let sucessos = 0;

        for (let i = 0; i < laudosComAlteracoes.length; i++) {
            const item = laudosComAlteracoes[i];
            try {
                // Grava SOMENTE o campo `apontamentos` (com o acima/abaixo) direto no documento, via update
                // parcial do Firestore. Não passamos pela entidade nem por `save()`, então nada de
                // cliente/fazenda/imagens é tocado — a gravação é cirúrgica e preserva o restante do laudo.
                await LaudoVisita.collection.doc(item.uid).update({
                    apontamentos: item.resultado.apontamentos,
                });
                sucessos++;
            } catch (error) {
                console.error(`Erro ao salvar laudo Nº ${item.sequencialIndex}:`, error);
                falhas.push(item.sequencialIndex);
            } finally {
                setProgresso({ atual: i + 1, total: laudosComAlteracoes.length });
            }
        }

        setMensagem({
            severity: falhas.length ? "warning" : "success",
            texto: falhas.length
                ? `${sucessos} laudo(s) atualizado(s). Falha em ${falhas.length}: Nº ${falhas.join(", ")}.`
                : `${sucessos} laudo(s) atualizado(s) com sucesso.`,
        });
        setDialogAberto(false);
        setPreview(null);
        setSalvando(false);
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Alert severity="warning">
                Esta ferramenta adiciona <strong>apenas os campos acima/abaixo</strong> aos apontamentos de{" "}
                <strong>todos os laudos do período</strong> informado, usando os parâmetros do cliente{" "}
                <strong>salvos em cada laudo</strong> (da época em que ele foi feito) — nunca os parâmetros atuais do
                cliente, que podem ter mudado desde então. A conformidade já existente <strong>não é alterada</strong>.
                Sempre revise o comparativo antes de confirmar — a gravação é imediata e definitiva. Para períodos
                longos, prefira rodar <strong>mês a mês</strong>.
            </Alert>

            <Card>
                <CardContent sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                    <TextField
                        label="Data de Início"
                        type="date"
                        value={dataInicio}
                        onChange={(e) => setDataInicio(e.target.value)}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="Data de Fim"
                        type="date"
                        value={dataFim}
                        onChange={(e) => setDataFim(e.target.value)}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="Status"
                        select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        size="small"
                        sx={{ minWidth: 180 }}
                    >
                        <MenuItem value="">Todos (exceto Cancelado)</MenuItem>
                        <MenuItem value="Em Digitação">Em Digitação</MenuItem>
                        <MenuItem value="Concluído">Concluído</MenuItem>
                    </TextField>
                    <Button variant="contained" onClick={handleBuscar} disabled={buscando}>
                        {buscando ? <CircularProgress size={20} /> : "Buscar Laudos"}
                    </Button>
                </CardContent>
            </Card>

            {erroBusca ? <Alert severity="warning">{erroBusca}</Alert> : null}
            {mensagem ? <Alert severity={mensagem.severity}>{mensagem.texto}</Alert> : null}

            {salvando ? (
                <Card>
                    <CardContent>
                        <Typography variant="body2" gutterBottom>
                            Gravando {progresso.atual} de {progresso.total} laudo(s)...
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={progresso.total ? (progresso.atual / progresso.total) * 100 : 0}
                        />
                    </CardContent>
                </Card>
            ) : null}

            {preview ? (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {preview.length} laudo(s) no período — {laudosComAlteracoes.length} com alterações
                        </Typography>

                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                            <Chip
                                color="warning"
                                label={`Com alterações: ${contarPorSituacao(SITUACAO.COM_ALTERACOES)}`}
                            />
                            <Chip
                                color="success"
                                variant="outlined"
                                label={`Já preenchidos: ${contarPorSituacao(SITUACAO.SEM_ALTERACOES)}`}
                            />
                            <Chip
                                color="error"
                                variant="outlined"
                                label={`Sem parâmetros: ${contarPorSituacao(SITUACAO.SEM_PARAMETROS)}`}
                            />
                            <Chip
                                color="error"
                                variant="outlined"
                                label={`Erros: ${contarPorSituacao(SITUACAO.ERRO)}`}
                            />
                        </Box>

                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Nº Visita</TableCell>
                                    <TableCell>Cliente</TableCell>
                                    <TableCell>Fazenda</TableCell>
                                    <TableCell>Situação</TableCell>
                                    <TableCell align="right">Alterações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {preview.map((l) => (
                                    <TableRow key={l.uid}>
                                        <TableCell>{l.sequencialIndex ?? "-"}</TableCell>
                                        <TableCell>{l.clienteNome}</TableCell>
                                        <TableCell>{l.fazendaNome}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                color={CHIP_SITUACAO[l.situacao].color}
                                                variant={l.situacao === SITUACAO.COM_ALTERACOES ? "filled" : "outlined"}
                                                label={CHIP_SITUACAO[l.situacao].label}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            {l.situacao === SITUACAO.COM_ALTERACOES ? l.totalAlteracoes : "-"}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        <Button
                            variant="contained"
                            color="warning"
                            sx={{ mt: 2 }}
                            onClick={() => setDialogAberto(true)}
                            disabled={salvando || laudosComAlteracoes.length === 0}
                        >
                            Confirmar e Salvar ({laudosComAlteracoes.length} laudo(s), {totalAlteracoes} alteração(ões))
                        </Button>
                    </CardContent>
                </Card>
            ) : null}

            <Dialog open={dialogAberto} onClose={() => (salvando ? null : setDialogAberto(false))}>
                <DialogTitle>Confirmar gravação em lote</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {laudosComAlteracoes.length} laudo(s) serão alterados permanentemente no banco de dados (
                        {totalAlteracoes} alteração(ões) de acima/abaixo no total). Esta ação grava direto no Firestore de
                        produção e não pode ser desfeita automaticamente. Confirma a gravação?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogAberto(false)} disabled={salvando}>
                        Cancelar
                    </Button>
                    <Button onClick={handleConfirmarSalvar} color="warning" variant="contained" disabled={salvando}>
                        {salvando ? <CircularProgress size={20} /> : "Confirmar e Salvar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
